import { useState } from 'react';
import { Search, Book, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Edition {
  google_books_id: string;
  title: string;
  subtitle?: string;
  authors: string[];
  publisher?: string;
  published_year?: number;
  page_count?: number;
  isbn13?: string;
  isbn10?: string;
  cover_url?: string;
  synopsis?: string;
}

interface BookIdentityFixerProps {
  bookId: string;
  bookTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFixed: () => void;
}

export function BookIdentityFixer({ bookId, bookTitle, open, onOpenChange, onFixed }: BookIdentityFixerProps) {
  const [searchQuery, setSearchQuery] = useState(bookTitle);
  const [editions, setEditions] = useState<Edition[]>([]);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const { toast } = useToast();

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fix-book-identity', {
        body: {
          action: 'search-editions',
          searchQuery: searchQuery
        }
      });

      if (error) throw error;

      setEditions(data.editions || []);
      
      if (data.editions.length === 0) {
        toast({
          title: 'No results',
          description: 'No editions found. Try a different search.',
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        variant: 'destructive',
        title: 'Search failed',
        description: 'Could not search for editions.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEdition = async (edition: Edition) => {
    setFixing(true);
    try {
      const { data, error } = await supabase.functions.invoke('fix-book-identity', {
        body: {
          action: 'fix-identity',
          bookId: bookId,
          newBookData: edition
        }
      });

      if (error) throw error;

      toast({
        title: 'Book fixed!',
        description: 'The correct edition has been set.',
      });

      onFixed();
      onOpenChange(false);
    } catch (error) {
      console.error('Fix error:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to fix',
        description: 'Could not update book identity.',
      });
    } finally {
      setFixing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fix Book Identity</DialogTitle>
          <DialogDescription>
            Search for the correct edition of this book
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search by title and author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {!loading && editions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Book className="w-12 h-12 mb-4 opacity-50" />
              <p>Search for the correct edition above</p>
            </div>
          )}

          <div className="grid gap-3">
            {editions.map((edition) => (
              <Card key={edition.google_books_id} className="hover:border-primary transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {edition.cover_url && (
                      <img
                        src={edition.cover_url}
                        alt={edition.title}
                        className="w-16 h-24 object-cover rounded shrink-0"
                      />
                    )}
                    <div className="flex-1 overflow-hidden">
                      <h3 className="font-semibold break-words">{edition.title}</h3>
                      {edition.subtitle && (
                        <p className="text-sm text-muted-foreground break-words">{edition.subtitle}</p>
                      )}
                      <p className="text-sm text-muted-foreground mt-1 break-words">
                        {edition.authors.join(', ')}
                      </p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {edition.published_year && (
                          <Badge variant="outline">{edition.published_year}</Badge>
                        )}
                        {edition.page_count && (
                          <Badge variant="outline">{edition.page_count} pages</Badge>
                        )}
                        {edition.isbn13 && (
                          <Badge variant="outline" className="max-w-full truncate">
                            ISBN: {edition.isbn13}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleSelectEdition(edition)}
                      disabled={fixing}
                      size="sm"
                      className="shrink-0 self-start sm:self-center"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Select
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}